import React from 'react';
import { capturePortalError } from '../lib/portalErrorReporting';
import { isStaleChunkLoadError } from '../lib/staleChunkRecovery';

type ReportStatus = 'logging' | 'logged' | 'queued';
type StaleChunkRecovery = 'idle' | 'blocked';

interface State {
  error: Error | null;
  info: React.ErrorInfo | null;
  reportStatus: ReportStatus;
  referenceId: string;
  staleChunkRecovery: StaleChunkRecovery;
  recoveryAlreadyTried: boolean;
}

const RECOVERY_PREFIX = 'portal-error-recovery-v2:';
const RECOVERY_WINDOW_MS = 5 * 60 * 1000;

function currentLocation(): string {
  return typeof window !== 'undefined' ? window.location.pathname : '';
}

function recoveryKey(error: Error): string {
  // One failed stylesheet may expose a different failed module after reload.
  // Treat them as one recovery, not a fresh invitation to reload for each hash.
  const failure = isStaleChunkLoadError(error) ? 'deployed-assets' : `${error.name}|${error.message}`;
  const value = `${currentLocation()}|${failure}`;
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `${RECOVERY_PREFIX}${(hash >>> 0).toString(16)}`;
}

function wasRecoveryRecentlyTried(error: Error): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const attemptedAt = Number(window.sessionStorage.getItem(recoveryKey(error)) || 0);
    return attemptedAt > 0 && Date.now() - attemptedAt < RECOVERY_WINDOW_MS;
  } catch {
    return false;
  }
}

function rememberRecoveryAttempt(error: Error) {
  try {
    window.sessionStorage.setItem(recoveryKey(error), String(Date.now()));
  } catch {
    // Recovery still works when browser storage is unavailable.
  }
}

export class GlobalErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  private unmounted = false;

  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = this.createInitialState();
  }

  private createInitialState(): State {
    return {
      error: null,
      info: null,
      reportStatus: 'logging',
      referenceId: '',
      staleChunkRecovery: 'idle',
      recoveryAlreadyTried: false,
    };
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('Portal error boundary caught', error, info);
    const recoveryAlreadyTried = wasRecoveryRecentlyTried(error);
    this.setState({
      info,
      recoveryAlreadyTried,
      staleChunkRecovery: isStaleChunkLoadError(error) ? 'blocked' : 'idle',
    }, () => {
      void this.logError(error, info, recoveryAlreadyTried);
    });
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  private safeSetState(update: Partial<State>) {
    if (this.unmounted) return;
    this.setState((previous) => ({ ...previous, ...update }));
  }

  private async logError(
    error: Error,
    info: React.ErrorInfo,
    recoveryAlreadyTried: boolean,
  ) {
    const staleChunk = isStaleChunkLoadError(error);
    const receipt = await capturePortalError({
      source: 'react_error_boundary',
      error,
      componentStack: info.componentStack || '',
      operation: staleChunk ? 'load_deployed_chunk' : 'render_portal',
      classification: staleChunk ? 'connectivity' : 'portal_defect',
      severity: staleChunk ? 'warning' : 'error',
      recoveryAttempted: recoveryAlreadyTried,
    });
    this.safeSetState(receipt
      ? { reportStatus: 'logged', referenceId: receipt.errorId }
      : { reportStatus: 'queued' });
  }

  render() {
    const {
      error,
      reportStatus,
      referenceId,
      staleChunkRecovery,
      recoveryAlreadyTried,
    } = this.state;
    if (!error) return this.props.children;

    const handleReload = () => {
      rememberRecoveryAttempt(error);
      window.location.reload();
    };
    const handleBack = () => {
      if (window.history.length > 1) window.history.back();
      else window.location.assign('/');
    };
    const handleHome = () => window.location.assign('/');
    const isAppUpdate = staleChunkRecovery !== 'idle';

    return (
      <main className="section-shell" style={{ margin: '2rem auto', maxWidth: 640 }}>
        <h1 style={{ marginTop: 0, fontSize: '1.4rem' }}>
          {isAppUpdate ? 'App update needed' : 'Portal encountered an error'}
        </h1>
        <p className="text-sm fg-muted" style={{ marginBottom: '1rem' }}>
          {recoveryAlreadyTried
            ? 'Reloading did not clear this problem. Go back or return to Portal home to continue.'
            : isAppUpdate
              ? 'Some Portal files could not be loaded. Reload once when you are ready to continue.'
              : 'We logged this automatically. Reload once, or go back and continue from the previous page.'}
        </p>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
          {!recoveryAlreadyTried ? (
            <button type="button" className="btn primary" onClick={handleReload}>
              {isAppUpdate ? 'Reload now' : 'Try again'}
            </button>
          ) : null}
          <button type="button" className="btn" onClick={handleBack}>Go back</button>
          <button type="button" className="btn" onClick={handleHome}>Portal home</button>
        </div>
        <p className="text-xs fg-muted" style={{ margin: 0 }} role="status">
          {reportStatus === 'logging'
            ? 'Logging this error automatically…'
            : reportStatus === 'queued'
              ? 'This error is queued and will be logged automatically when the connection is available.'
              : `Error logged automatically. Reference: ${referenceId}`}
        </p>
      </main>
    );
  }
}
